/**
 * Product Catalogue Analysis Report
 *
 * Cross-FP catalogue exploration tool. Combines FP filters with product,
 * stock and accounting filters to surface catalogue hygiene questions.
 *
 * Three result views:
 * - Detail: one row per sellable SKU (the default table).
 * - FP Summary: one row per FP with per-axis counts (mirrors the chips).
 * - Info: the filters that were applied to this run.
 */
import { Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import BaseReportPage, { ColumnDef, ColumnGroup, ResultTab } from './BaseReportPage'
import {
  FP_IDENTITY, PRODUCT_IDENTITY, PRICING_STOCK, XERO_ACCOUNTS,
} from './columns/productColumns'
import { formatCellValue } from '../../../../utils/tableFormatters'

const FP_SUMMARY_COLUMNS: ColumnDef[] = [
  { key: 'fp_code', label: 'FP Code', type: 'text' },
  { key: 'franchise_name', label: 'Franchise Name', type: 'text', align: 'left', minWidth: 200 },
  { key: 'fp_status', label: 'FP Status', type: 'text' },
  { key: 'market', label: 'Market', type: 'text' },
  { key: 'products', label: 'Products', type: 'integer', align: 'right' },
  { key: 'inv_off', label: 'Inv Off', type: 'integer', align: 'right' },
]

const columns: ColumnDef[] = [
  ...FP_IDENTITY,
  ...PRODUCT_IDENTITY,
  ...PRICING_STOCK,
  ...XERO_ACCOUNTS,
]

const columnGroups: ColumnGroup[] = [
  { label: 'FP', columnKeys: FP_IDENTITY.map(c => c.key) },
  { label: 'Product', columnKeys: PRODUCT_IDENTITY.map(c => c.key) },
  { label: 'Pricing & Stock', columnKeys: PRICING_STOCK.map(c => c.key) },
  { label: 'Xero Accounts', columnKeys: XERO_ACCOUNTS.map(c => c.key) },
]

/**
 * Per-FP rollup of the same filtered set. Rows come from
 * ``result_summary.fp_summary`` (populated server-side at execute time).
 */
function FpSummaryTab({ rows }: { rows: Record<string, any>[] }) {
  if (!rows || rows.length === 0) {
    return <Typography color="text.secondary">No rows match the current filters.</Typography>
  }
  return (
    <Paper variant="outlined">
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {FP_SUMMARY_COLUMNS.map(col => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: '#007f98',
                    color: 'white',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    textAlign: col.align ?? 'center',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={row.fp_code ?? idx} sx={{ '&:hover': { backgroundColor: '#E6F4F5' } }}>
                {FP_SUMMARY_COLUMNS.map(col => (
                  <TableCell key={col.key} sx={{ textAlign: col.align ?? 'center' }}>
                    {formatCellValue(row[col.key], col, row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

// Map raw filter dict keys (snake_case as sent to API) to human labels.
// Kept local because the values are display-only and may diverge from
// the camelCase FILTER_LABELS in shared filter types.
const INFO_FILTER_LABELS: Record<string, string> = {
  fp_code: 'FP',
  fp_status: 'FP Status',
  market: 'Market',
  region: 'Region',
  product_search: 'Name or SKU',
  product_status: 'Product Status',
  product_type: 'Product Type',
  main_category: 'Main Category',
  inventory_tracking: 'Inventory Tracking',
  stock: 'Stock',
  last_sold_aging: 'Last Sold',
  xero_sales_account: 'Xero Sales Account',
  xero_sales_account_code: 'Sales Code',
  xero_purchase_account: 'Xero Purchase Account',
  xero_purchase_account_code: 'Purchase Code',
}

/**
 * Info tab - shows which filters drove this run so users coming back to
 * a past job (or sharing the link) can see what was searched without
 * re-deriving from the live filter card.
 */
function InfoTab({ job }: { job: any }) {
  const filters = (job?.filters || {}) as Record<string, any>
  const entries = Object.entries(filters).filter(
    ([k, v]) => k !== 'user_role' && v !== undefined && v !== null && v !== '',
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Applied Filters
        </Typography>
        {entries.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            No filters - report ran across the full catalogue.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {entries.map(([key, value]) => (
              <Chip
                key={key}
                size="small"
                variant="outlined"
                label={`${INFO_FILTER_LABELS[key] ?? key}: ${value}`}
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        )}
      </Box>
      {job?.created_at && (
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Run Details
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Generated: {new Date(job.created_at).toLocaleString()}<br />
            Rows returned: {job.result_row_count?.toLocaleString() ?? '-'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

const extraResultTabs: ResultTab[] = [
  {
    key: 'fp_summary',
    label: 'FP Summary',
    render: (job) => <FpSummaryTab rows={job?.result_summary?.fp_summary ?? []} />,
  },
  {
    key: 'info',
    label: 'Info',
    render: (job) => <InfoTab job={job} />,
  },
]

export default function ProductCatalogueAnalysisReport() {
  return (
    <BaseReportPage
      reportType="product_catalogue_analysis"
      title="Product Catalogue Analysis"
      description="Cross-FP product catalogue analysis. Combine FP filters with product, stock and accounting filters to investigate catalogue hygiene questions across all franchise partners."
      filterConfig={{
        showFpSelector: true,
        showFpStatus: true,
        showCountry: true,
        showRegion: true,
        linkFpStatusToSearch: true,
        showProductFilters: true,
      }}
      columns={columns}
      columnGroups={columnGroups}
      defaultSortColumn="fp_code"
      extraResultTabs={extraResultTabs}
    />
  )
}
